import { computed, ref } from "vue"
import { defineStore } from "pinia"
import { UseRootStore } from "@/model/RootStore"
import {
	ContactResearchEventType,
	ContactResearchProviderProgressStatus,
	ContactResearchRunStatus,
	type ContactResearchEvent,
	type ContactResearchInput,
	type ContactResearchProviderId,
	type ContactResearchProviderInfo,
	type ContactResearchProviderProgress,
	type ContactResearchResult,
} from "@/types/contactResearch"
import * as apiClient from "@/utils/ApiClient"

const RUN_POLL_INTERVAL_MS = 5_000
const MAX_CONSECUTIVE_POLL_ERRORS = 5

function providerProgress(provider: ContactResearchProviderInfo): ContactResearchProviderProgress {
	return {
		provider,
		status: ContactResearchProviderProgressStatus.Pending,
		logs: [],
		output: "",
		result: null,
		startedAt: null,
	}
}

export const useContactResearchStore = defineStore("contactResearch", () => {
	const rootStore = UseRootStore()
	const providers = ref<ContactResearchProviderInfo[]>([])
	const providerProgressById = ref<Partial<Record<ContactResearchProviderId, ContactResearchProviderProgress>>>({})
	const runId = ref<string | null>(null)
	const runStartedAt = ref<string | null>(null)
	const result = ref<ContactResearchResult | null>(null)
	const error = ref<string | null>(null)
	const status = ref<ContactResearchRunStatus>(ContactResearchRunStatus.Idle)
	const isRunning = computed(() => status.value === ContactResearchRunStatus.Running)
	let eventStreamController: AbortController | null = null
	let runSequence = 0

	function setProviderProgress(nextProviders: ContactResearchProviderInfo[]): void {
		providerProgressById.value = Object.fromEntries(
			nextProviders.map(provider => [provider.id, providerProgress(provider)])
		)
	}

	function handleEvent(event: ContactResearchEvent): void {
		if (event.type === ContactResearchEventType.RunStarted) {
			runStartedAt.value = event.timestamp
			setProviderProgress(event.providers)
		}

		if (event.type === ContactResearchEventType.ProviderStarted) {
			providerProgressById.value[event.provider.id] = {
				...(providerProgressById.value[event.provider.id] ?? providerProgress(event.provider)),
				status: ContactResearchProviderProgressStatus.Running,
				startedAt: event.timestamp,
			}
		}

		if (event.type === ContactResearchEventType.ProviderLog) {
			const progress = providerProgressById.value[event.provider]

			if (progress) {
				progress.logs.push({ message: event.message, timestamp: event.timestamp })
			}
		}

		if (event.type === ContactResearchEventType.ProviderOutputDelta) {
			const progress = providerProgressById.value[event.provider]

			if (progress) {
				progress.output += event.delta
			}
		}

		if (event.type === ContactResearchEventType.ProviderCompleted) {
			const progress = providerProgressById.value[event.result.provider]

			if (progress) {
				progress.status = event.result.status
				progress.result = event.result
			}
		}

		if (event.type === ContactResearchEventType.RunCompleted) {
			applyResult(event.result)
		}

		if (event.type === ContactResearchEventType.RunFailed) {
			error.value = event.error
			status.value = ContactResearchRunStatus.Failed
		}
	}

	function applyResult(completedResult: ContactResearchResult): void {
		for (const providerResult of completedResult.providers) {
			const progress = providerProgressById.value[providerResult.provider]

			if (progress) {
				progress.status = providerResult.status
				progress.result = providerResult

				if (progress.logs.length === 0) {
					progress.logs = providerResult.logs.map(message => ({
						message,
						timestamp: completedResult.completedAt,
					}))
				}
			}
		}

		result.value = completedResult
		error.value = null
		status.value = ContactResearchRunStatus.Completed
	}

	function waitForNextPoll(signal: AbortSignal): Promise<void> {
		return new Promise(resolve => {
			const timer = setTimeout(resolve, RUN_POLL_INTERVAL_MS)
			signal.addEventListener(
				"abort",
				() => {
					clearTimeout(timer)
					resolve()
				},
				{ once: true }
			)
		})
	}

	async function waitForRunCompletion(
		completedRunId: string,
		currentRunSequence: number,
		signal: AbortSignal
	): Promise<void> {
		let consecutiveErrors = 0
		let lastError: unknown = null

		while (currentRunSequence === runSequence && !signal.aborted) {
			try {
				const run = await apiClient.getContactResearchRun(rootStore.userApiToken, completedRunId)
				consecutiveErrors = 0

				if (run.result) {
					applyResult(run.result)

					return
				}

				if (run.error) {
					error.value = run.error
					status.value = ContactResearchRunStatus.Failed

					return
				}
			} catch (pollError) {
				lastError = pollError
				consecutiveErrors += 1

				if (consecutiveErrors >= MAX_CONSECUTIVE_POLL_ERRORS) {
					throw lastError
				}
			}

			await waitForNextPoll(signal)
		}
	}

	function clearRun(): void {
		runSequence += 1
		eventStreamController?.abort()
		eventStreamController = null
		runId.value = null
		runStartedAt.value = null
		result.value = null
		error.value = null
		status.value = ContactResearchRunStatus.Idle
		providerProgressById.value = {}
	}

	async function loadProviders(): Promise<ContactResearchProviderInfo[]> {
		const loadedProviders = await apiClient.getContactResearchProviders(rootStore.userApiToken)
		providers.value = loadedProviders

		return loadedProviders
	}

	async function startRun(input: ContactResearchInput, providerIds: ContactResearchProviderId[]): Promise<void> {
		clearRun()
		const currentRunSequence = runSequence
		status.value = ContactResearchRunStatus.Running
		let controller: AbortController | null = null
		let streamError: unknown = null

		try {
			const startedRun = await apiClient.startContactResearchRun(rootStore.userApiToken, input, providerIds)

			if (currentRunSequence !== runSequence) {
				return
			}

			runId.value = startedRun.runId
			runStartedAt.value = new Date().toISOString()
			setProviderProgress(startedRun.providers)
			controller = new AbortController()
			eventStreamController = controller
			try {
				await apiClient.streamContactResearchEvents(
					rootStore.userApiToken,
					startedRun.runId,
					event => {
						if (currentRunSequence === runSequence) {
							handleEvent(event)
						}
					},
					controller.signal
				)
			} catch (error) {
				streamError = error
			}

			if (currentRunSequence !== runSequence) {
				return
			}

			if (status.value === ContactResearchRunStatus.Running) {
				try {
					await waitForRunCompletion(startedRun.runId, currentRunSequence, controller.signal)
				} catch (pollError) {
					throw streamError ?? pollError
				}
			}
		} catch (runError) {
			if (controller?.signal.aborted || currentRunSequence !== runSequence) {
				return
			}

			error.value = runError instanceof Error ? runError.message : String(runError)
			status.value = ContactResearchRunStatus.Failed
		} finally {
			if (eventStreamController === controller) {
				eventStreamController = null
			}
		}
	}

	return {
		providers,
		providerProgressById,
		runId,
		runStartedAt,
		result,
		error,
		status,
		isRunning,
		loadProviders,
		startRun,
		clearRun,
	}
})
